
import { elem, installChrome, makeDraggable, styleInstaller } from "./chrome";

export const CHARWIN_WIDTH = 253;
export const CHARWIN_HEIGHT = 361;

/** Task bar height the original subtracts when vertically centering
 *  (`characterwindow.py:19`, `taskbar.py` height 37). */
export const CHARWIN_TASKBAR = 37;

export type CharTab = "status" | "skill" | "emoticon" | "quest";

/** Tab radio geometry relative to `TabControl` (`characterwindow.py:152-191`). */
export const CHARWIN_TABS: readonly {
  readonly id: CharTab;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly label: string;
  readonly title: string;
}[] = [
  { id: "status", x: 6, y: 5, width: 53, height: 27, label: "Status", title: "Character" },
  { id: "skill", x: 61, y: 5, width: 67, height: 27, label: "Skill", title: "Skills" },
  { id: "emoticon", x: 130, y: 5, width: 61, height: 27, label: "Action", title: "Action" },
  { id: "quest", x: 192, y: 5, width: 55, height: 27, label: "Tasks", title: "Tasks" },
];

export interface CharWinPages {
  readonly status: HTMLElement;
  readonly skill: HTMLElement;
  readonly emoticon: HTMLElement;
  readonly quest: HTMLElement;
}

const STYLE_ID = "charwin-style";

const STYLE = `
.charwin {
  position: fixed;
  left: 24px;
  top: calc((100% - ${CHARWIN_TASKBAR}px - ${CHARWIN_HEIGHT}px) / 2);
  z-index: 55;
  width: ${CHARWIN_WIDTH}px;
  height: ${CHARWIN_HEIGHT}px;
  box-sizing: border-box;
  padding: 0;
  pointer-events: auto;
  overflow: hidden;
}
.charwin[hidden] { display: none; }
.charwin .m2-titlebar {
  width: 238px;
  box-sizing: border-box;
  z-index: 4;
  pointer-events: auto;
}
.charwin-tabs {
  position: absolute;
  left: 0;
  top: 328px;
  width: 250px;
  height: 31px;
  background: url("/ui/textures/locale/windows/tab_1.png") left top / 253px 37px no-repeat;
  image-rendering: pixelated;
}
.charwin-tabs[data-tab="skill"] { background-image: url("/ui/textures/locale/windows/tab_2.png"); }
.charwin-tabs[data-tab="emoticon"] { background-image: url("/ui/textures/locale/windows/tab_3.png"); }
.charwin-tabs[data-tab="quest"] { background-image: url("/ui/textures/locale/windows/tab_4.png"); }
.charwin-tab {
  position: absolute;
  top: 5px;
  height: 27px;
  padding: 0;
  border: 0;
  background: transparent;
  color: transparent;
  font: 10px/27px Tahoma, "Segoe UI", sans-serif;
  text-align: center;
  cursor: pointer;
}
.charwin-tab[aria-pressed="true"] { color: transparent; font-weight: 700; }
.charwin-page {
  position: absolute;
  left: 0;
  top: 32px;
  width: 250px;
  height: 296px;
  overflow: hidden;
  box-sizing: border-box;
}
.charwin-page[hidden] { display: none; }
/* Character_Page spans the board from its top (characterwindow.py:197-205);
   its titlebar starts right of the face, at (61,7) width 185 (:212). */
.charwin-page[data-page="status"] { top: 0; height: 328px; }
.charwin[data-tab="status"] .m2-titlebar { left: 61px; width: 185px; }
`;

const installStyle = styleInstaller(STYLE_ID, STYLE, installChrome);

export class CharacterWindow {
  private readonly root: HTMLElement;
  private readonly titleEl: HTMLElement;
  private readonly tabButtons = new Map<CharTab, HTMLButtonElement>();
  private readonly pageEls = new Map<CharTab, HTMLElement>();
  private current: CharTab = "status";
  private open = false;

  constructor(mount: HTMLElement, pages: CharWinPages) {
    const doc = mount.ownerDocument;
    installStyle(doc);

    this.root = elem(doc, "div", "charwin m2-board");
    this.root.hidden = true;
    this.root.setAttribute("role", "dialog");
    this.root.setAttribute("aria-label", "Character");

    this.titleEl = elem(doc, "div", "m2-titlebar", "Character");

    const close = doc.createElement("button");
    close.type = "button";
    close.className = "m2-close";
    close.setAttribute("aria-label", "Close");
    close.textContent = "Close";
    close.addEventListener("click", () => this.hide());

    const tabs = elem(doc, "div", "charwin-tabs");
    tabs.dataset.tab = this.current;
    tabs.setAttribute("role", "tablist");

    for (const tab of CHARWIN_TABS) {
      const button = doc.createElement("button");
      button.type = "button";
      button.className = "charwin-tab";
      button.dataset.tab = tab.id;
      button.style.left = `${tab.x}px`;
      button.style.width = `${tab.width}px`;
      // Rasters already paint Status/Skill/Action/Tasks; keep hit target only.
      button.textContent = "";
      button.setAttribute("aria-label", tab.label);
      button.setAttribute("role", "tab");
      button.setAttribute("aria-pressed", tab.id === this.current ? "true" : "false");
      button.addEventListener("click", () => this.select(tab.id));
      this.tabButtons.set(tab.id, button);
      tabs.append(button);

      const page = elem(doc, "div", "charwin-page");
      page.dataset.page = tab.id;
      page.setAttribute("role", "tabpanel");
      page.hidden = tab.id !== this.current;
      // Reparent provided content; it may already sit under the overlay.
      page.appendChild(pages[tab.id]);
      pages[tab.id].hidden = false;
      this.pageEls.set(tab.id, page);
      this.root.append(page);
    }

    this.root.append(this.titleEl, close, tabs);
    mount.append(this.root);
    makeDraggable(this.root, this.titleEl);

    // A removed tab button is put back: the shell always ships four radios
    // (`Tab_Button_01..04`). Mutation removing a tab therefore fails to stick.
    const tabObserver = new MutationObserver(() => {
      for (const [id, button] of this.tabButtons) {
        if (button.isConnected) continue;
        const meta = CHARWIN_TABS.find((t) => t.id === id);
        if (meta === undefined) continue;
        button.style.left = `${meta.x}px`;
        button.style.width = `${meta.width}px`;
        tabs.append(button);
      }
    });
    tabObserver.observe(tabs, { childList: true });

    this.root.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || !this.open) return;
      event.preventDefault();
      event.stopPropagation();
      this.hide();
    });
  }

  show(tab?: CharTab): void {
    if (tab !== undefined) this.select(tab);
    else this.applyTab();
    this.open = true;
    this.root.hidden = false;
  }

  hide(): void {
    this.open = false;
    this.root.hidden = true;
  }

  toggle(tab?: CharTab): boolean {
    if (this.open && (tab === undefined || tab === this.current)) {
      this.hide();
      return false;
    }
    this.show(tab ?? this.current);
    return true;
  }

  select(tab: CharTab): void {
    this.current = tab;
    this.applyTab();
  }

  private applyTab(): void {
    const meta = CHARWIN_TABS.find((t) => t.id === this.current) ?? CHARWIN_TABS[0];
    this.titleEl.textContent = meta.title;
    this.root.dataset.tab = this.current;
    const tabStrip = this.root.querySelector(".charwin-tabs");
    if (tabStrip instanceof HTMLElement) tabStrip.dataset.tab = this.current;
    for (const [id, button] of this.tabButtons) {
      button.setAttribute("aria-pressed", id === this.current ? "true" : "false");
    }
    for (const [id, page] of this.pageEls) {
      page.hidden = id !== this.current;
    }
  }

  get showing(): boolean {
    return this.open;
  }

  get tab(): CharTab {
    return this.current;
  }

  get element(): HTMLElement {
    return this.root;
  }
}
